import { __rest } from 'tslib';
import { jsx } from 'react/jsx-runtime';
import React, { createContext } from 'react';
import classNames from 'classnames';
import { B as BREAK_POINTS, G as GRID_COLUMNS } from './constant-f622fb0e.js';
import { CommonStyles } from './components/shared/BaseProps.es.js';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

const RowContext = createContext({});

const responsiveArray = [
    'xxl',
    'xl',
    'lg',
    'md',
    'sm',
    'xs',
];
const responsiveMap = {
    xs: '(max-width: 575px)',
    sm: '(min-width: 576px)',
    md: '(min-width: 768px)',
    lg: '(min-width: 1024px)',
    xl: '(min-width: 1440px)',
    xxl: '(min-width: 1600px)',
};
const subscribers = new Map();
let subUid = -1;
let screens = {};
const responsiveObserve = {
    matchHandlers: {},
    dispatch(pointMap) {
        screens = pointMap;
        subscribers.forEach((func) => func(screens));
        return subscribers.size >= 1;
    },
    subscribe(func) {
        if (!subscribers.size)
            this.register();
        subUid += 1;
        subscribers.set(subUid, func);
        func(screens);
        return subUid;
    },
    unsubscribe(token) {
        subscribers.delete(token);
        if (!subscribers.size)
            this.unregister();
    },
    unregister() {
        Object.keys(responsiveMap).forEach((screen) => {
            const matchMediaQuery = responsiveMap[screen];
            const handler = this.matchHandlers[matchMediaQuery];
            handler === null || handler === void 0 ? void 0 : handler.mql.removeListener(handler === null || handler === void 0 ? void 0 : handler.listener);
        });
        subscribers.clear();
    },
    register() {
        Object.keys(responsiveMap).forEach((screen) => {
            const matchMediaQuery = responsiveMap[screen];
            const listener = ({ matches }) => {
                this.dispatch(Object.assign(Object.assign({}, screens), { [screen]: matches }));
            };
            const mql = window.matchMedia(matchMediaQuery);
            mql.addListener(listener);
            this.matchHandlers[matchMediaQuery] = {
                mql,
                listener,
            };
            listener(mql);
        });
    },
};

const Row = React.forwardRef((props, ref) => {
    const { justify, align, className, style, children, gutter = 0, wrap } = props, others = __rest(props, ["justify", "align", "className", "style", "children", "gutter", "wrap"]);
    const [screens, setScreens] = React.useState({
        xs: true,
        sm: true,
        md: true,
        lg: true,
        xl: true,
        xxl: true,
    });
    const gutterRef = React.useRef(gutter);
    React.useEffect(() => {
        const token = responsiveObserve.subscribe((screen) => {
            const currentGutter = gutterRef.current || 0;
            if ((!Array.isArray(currentGutter) &&
                typeof currentGutter === 'object') ||
                (Array.isArray(currentGutter) &&
                    (typeof currentGutter[0] === 'object' ||
                        typeof currentGutter[1] === 'object'))) {
                setScreens(screen);
            }
        });
        return () => responsiveObserve.unsubscribe(token);
    }, []);
    const getGutter = () => {
        const results = [0, 0];
        const normalizedGutter = Array.isArray(gutter) ? gutter : [gutter, 0];
        normalizedGutter.forEach((g, index) => {
            if (typeof g === 'object') {
                for (let i = 0; i < responsiveArray.length; i++) {
                    const breakpoint = responsiveArray[i];
                    if (screens[breakpoint] && g[breakpoint] !== undefined) {
                        results[index] = g[breakpoint];
                        break;
                    }
                }
            }
            else {
                results[index] = g || 0;
            }
        });
        return results;
    };
    const prefixCls = 'teko-row';
    const gutters = getGutter();
    const classes = classNames(prefixCls, {
        [`${prefixCls}-no-wrap`]: wrap === false,
        [`${prefixCls}-${justify}`]: justify,
        [`${prefixCls}-${align}`]: align,
    }, className);
    const rowStyle = Object.assign(Object.assign(Object.assign({}, (gutters[0] && gutters[0] > 0
        ? {
            marginLeft: gutters[0] / -2,
            marginRight: gutters[0] / -2,
        }
        : {})), (gutters[1] && gutter[1] > 0
        ? {
            rowGap: gutters[1],
        }
        : {})), style);
    return (jsx(RowContext.Provider, Object.assign({ value: { gutter: gutters, wrap } }, { children: jsx(StyledRow, Object.assign({}, others, { className: classes, style: rowStyle, ref: ref }, { children: children })) })));
});
Row.displayName = 'Row';
const StyledRow = styled.div `
  &.teko-row {
    display: flex;
    flex-flow: row wrap;
  }
  @media (min-width: ${BREAK_POINTS.lg}) {
    &.teko-row {
      justify-content: center;
    }
  }
  &.teko-row:before,
  &.teko-row:after {
    display: flex;
  }

  // No wrap of flex
  &.teko-row-no-wrap {
    flex-wrap: nowrap;
  }

  &.teko-row-start {
    justify-content: flex-start;
  }

  &.teko-row-center {
    justify-content: center;
  }

  &.teko-row-end {
    justify-content: flex-end;
  }

  &.teko-row-space-between {
    justify-content: space-between;
  }

  &.teko-row-space-around {
    justify-content: space-around;
  }

  &.teko-row-top {
    align-items: flex-start;
  }

  &.teko-row-middle {
    align-items: center;
  }

  &.teko-row-bottom {
    align-items: flex-end;
  }
  ${CommonStyles}
`;

function parseFlex(flex) {
    if (typeof flex === 'number') {
        return `${flex} ${flex} auto`;
    }
    if (/^\d+(\.\d+)?(px|em|rem|%)$/.test(flex)) {
        return `0 0 ${flex}`;
    }
    return flex;
}
const sizes = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
const Col = React.forwardRef((props, ref) => {
    const { gutter, wrap } = React.useContext(RowContext);
    const { span, order, offset, push, pull, className, children, flex, style } = props, others = __rest(props, ["span", "order", "offset", "push", "pull", "className", "children", "flex", "style"]);
    const prefixCls = 'teko-col';
    let sizeClassObj = {};
    sizes.forEach((size) => {
        let sizeProps = {};
        const propSize = props[size];
        if (typeof propSize === 'number') {
            sizeProps.span = propSize;
        }
        else if (typeof propSize === 'object') {
            sizeProps = propSize || {};
        }
        sizeClassObj = Object.assign(Object.assign({}, sizeClassObj), { [`${prefixCls}-${size}-${sizeProps.span}`]: sizeProps.span !== undefined, [`${prefixCls}-${size}-order-${sizeProps.order}`]: sizeProps.order || sizeProps.order === 0, [`${prefixCls}-${size}-offset-${sizeProps.offset}`]: sizeProps.offset || sizeProps.offset === 0, [`${prefixCls}-${size}-push-${sizeProps.push}`]: sizeProps.push || sizeProps.push === 0, [`${prefixCls}-${size}-pull-${sizeProps.pull}`]: sizeProps.pull || sizeProps.pull === 0 });
    });
    const classes = classNames(prefixCls, {
        [`${prefixCls}-${span}`]: span !== undefined,
        [`${prefixCls}-order-${order}`]: order,
        [`${prefixCls}-offset-${offset}`]: offset,
        [`${prefixCls}-push-${push}`]: push,
        [`${prefixCls}-pull-${pull}`]: pull,
    }, className, sizeClassObj);
    let mergedStyle = Object.assign({}, style);
    if (gutter) {
        mergedStyle = Object.assign(Object.assign({}, (gutter[0] && gutter[0] > 0
            ? {
                paddingLeft: gutter[0] / 2,
                paddingRight: gutter[0] / 2,
            }
            : {})), mergedStyle);
    }
    if (flex) {
        mergedStyle.flex = parseFlex(flex);
        if (flex === 'auto' && wrap === false && !mergedStyle.minWidth) {
            mergedStyle.minWidth = 0;
        }
    }
    return (jsx(StyledCol, Object.assign({}, others, { style: mergedStyle, className: classes, ref: ref }, { children: children })));
});
Col.displayName = 'Col';
const floorPercentage = (percent) => {
    return Math.floor(percent * 100) / 100;
};
const percentage = (x, y) => {
    return floorPercentage((x / y) * 100) + '%';
};
const loopGridColumn = (_class = '') => {
    let _css = `
    &.teko-col${_class}-0 {
      display: none;
    }
    &.teko-col-push-0 {
      left: auto;
    }
    &.teko-col-pull-0 {
      right: auto;
    }
    &.teko-col${_class}-push-0 {
      left: auto;
    }
    &.teko-col${_class}-pull-0 {
      right: auto;
    }
    &.teko-col${_class}-offset-0 {
      margin-left: 0;
    }
    &.teko-col${_class}-order-0 {
      order: 0;
    }
  `;
    for (let i = 1; i <= 24; i++) {
        _css += `
      &.teko-col${_class}-${i} {
        display: block;
        flex: 0 0 ${percentage(i, GRID_COLUMNS)};
        max-width: ${_class === '-lg'
            ? '84px'
            : ['-xl', '-xxl'].includes(_class)
                ? '104px'
                : percentage(i, GRID_COLUMNS)};
      }
      &.teko-col${_class}-push-${i} {
        left: ${percentage(i, GRID_COLUMNS)};
      }
      &.teko-col${_class}-pull-${i} {
        right: ${percentage(i, GRID_COLUMNS)};
      }
      &.teko-col${_class}-offset-${i} {
        margin-left: ${percentage(i, GRID_COLUMNS)};
      }
      &.teko-col${_class}-order-${i} {
        order: ${i};
      }
    `;
    }
    return _css;
};
const makeGrid = css `
  ${loopGridColumn()}
  ${loopGridColumn('-xs')}
  ${Object.keys(BREAK_POINTS)
    .map((label) => label !== 'xs'
    ? `
    @media (min-width: ${BREAK_POINTS[label]}) {
      ${loopGridColumn(`-${label}`)}
    }
  `
    : '')
    .join('')}
`;
const flexStyle = (flex) => {
    if (!flex)
        return;
    return `
    flex: ${parseFlex(flex)};
    ${typeof flex === 'string' ? `max-width: ${flex} !important;` : ''}
  `;
};
const makeFlexStyles = (props) => {
    var _a;
    return css `
    ${typeof props.xs === 'object' && ((_a = props.xs) === null || _a === void 0 ? void 0 : _a.flex) !== undefined
        ? flexStyle(props.xs.flex)
        : ''}
    ${Object.keys(BREAK_POINTS)
        .map((label) => {
        var _a, _b;
        return ((_a = props[label]) === null || _a === void 0 ? void 0 : _a.flex) && label !== 'xs'
            ? `
      @media (min-width: ${BREAK_POINTS[label]}) {
        ${flexStyle((_b = props[label]) === null || _b === void 0 ? void 0 : _b.flex)};
      }
    `
            : '';
    })
        .join('')}
  `;
};
const StyledCol = styled.div(`
  &.teko-col {
    position: relative;
    max-width: 100%;
    // Prevent columns from collapsing when empty
    min-height: 1px;
  }`, CommonStyles, makeGrid, makeFlexStyles);

export { Col as C, Row as R, floorPercentage as f, percentage as p };
